import { useMutation, useQuery, useQueryClient } from "react-query";
import { shopHttp } from "./shop-http";
import { OrganizationKeyName } from "../../../model/Organization";
import { LinkProductToShopType } from "../../../service/api-response/LinkProductToShop";
import { UnlinkProductToShopType } from "../../../service/api-response/UnlinkProductFromShop";
import { ShopListItem } from "../model/ShopListSuccessResponse";
import { CreateShop, UpdateShop } from "../model/ShopSuccessResponse";

const cacheKeys = {
  products: "products",
  product: "product",
  shop: "shop",
  salesChannels: "salesChannels",
  salesChannel: "salesChannel",
  shops: "shops",
  productCategories: "productCategories",
  timeslots: "timeslots",
};

export const useGetSalesChannels = (
  organizationKeyName: OrganizationKeyName,
  shopId?: string
) => {
  return useQuery(
    [cacheKeys.salesChannels, shopId],
    () => shopHttp.getSalesChannels(organizationKeyName, shopId),
    // Sales channels are created and edited in the new admin, usually in
    // another tab, so refetch when the user comes back.
    { refetchOnWindowFocus: true, enabled: !!shopId }
  );
};

export const useGetSalesChannel = (
  organizationKeyName: OrganizationKeyName,
  id?: string,
  enabled = true
) => {
  return useQuery(
    [cacheKeys.salesChannel, id],
    () => shopHttp.getSalesChannel(organizationKeyName, id),
    { refetchOnWindowFocus: true, enabled: !!id && enabled }
  );
};

export const useLinkProductToShop = () => {
  const queryClient = useQueryClient();
  return useMutation<any, any, LinkProductToShopType>(
    shopHttp.linkProductToShop,
    {
      onSuccess: async () => {
        await queryClient.refetchQueries(cacheKeys.products);
      },
    }
  );
};

export const useUnlinkProductToShop = () => {
  const queryClient = useQueryClient();
  return useMutation<any, any, UnlinkProductToShopType>(
    shopHttp.unlinkProductToShop,
    {
      onSuccess: async () => {
        await queryClient.refetchQueries(cacheKeys.products);
      },
    }
  );
};

export const useGetShop = (
  organizationKeyName: OrganizationKeyName,
  id?: string
) => {
  return useQuery(
    [cacheKeys.shop, id],
    () => shopHttp.getShop(organizationKeyName, id),
    { refetchOnWindowFocus: false, enabled: !!id }
  );
};

export const useGetShops = (
  organizationKeyName: OrganizationKeyName,
  enabled: boolean = true
) => {
  return useQuery<ShopListItem[], any>(
    [cacheKeys.shops],
    () => shopHttp.getShops(organizationKeyName),
    { refetchOnWindowFocus: false, enabled }
  );
};

export const useCreateShop = () => {
  const queryClient = useQueryClient();
  return useMutation<string, any, CreateShop>(shopHttp.createShop, {
    onSuccess: async () => {
      queryClient.refetchQueries([cacheKeys.shops]);
    },
  });
};

export const useUpdateShop = (id?: string) => {
  const queryClient = useQueryClient();
  return useMutation<any, any, UpdateShop>(shopHttp.updateShop, {
    onSuccess: async () => {
      queryClient.refetchQueries([cacheKeys.shop, id]);
    },
  });
};

export const useListTimeslots = (
  organizationKeyName: OrganizationKeyName,
  shopId: string,
  enabled: boolean = true
) => {
  return useQuery(
    [cacheKeys.timeslots, shopId],
    () => shopHttp.listTimeslots(organizationKeyName, shopId),
    { refetchOnWindowFocus: false, enabled }
  );
};

export const useCopyAndSetTimeslotsFromShop = () => {
  const queryClient = useQueryClient();
  return useMutation(shopHttp.copyAndSetTimeslotsFromShop, {
    onSuccess: async () => {
      queryClient.refetchQueries([cacheKeys.timeslots]);
    },
  });
};

export const useCreateTimeslotTemplate = (shopId?: string) => {
  const queryClient = useQueryClient();
  return useMutation(shopHttp.createTimeslotTemplate, {
    onSuccess: async () => {
      // Seems like the creation is being handled async, so introducing a delay to increase the chance of the timeslots having been created before we refetch
      if (shopId) {
        setTimeout(() => {
          queryClient.refetchQueries([cacheKeys.timeslots, shopId]);
        }, 5000);
      }
    },
  });
};

export const useUpdateTimeslot = () => {
  const queryClient = useQueryClient();
  return useMutation(shopHttp.updateTimeslot, {
    onSuccess: async () => {
      queryClient.refetchQueries([cacheKeys.timeslots]);
    },
  });
};
